import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../../components/profile-components/Navbar";
import Topbar from "../../components/profile-components/Topbar";
import "./WorkflowEditor.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://agent-book-crm-backend.onrender.com";

const EMAIL_RECIPIENT_OPTIONS = {
  account: [
    { value: "record", label: "Record" },
    { value: "assigned_agent", label: "Assigned Agent" },
    { value: "account_admin", label: "Account Admin" },
    { value: "specific_account_user", label: "Specific Account User" },
  ],
  platform: [
    { value: "record", label: "Record" },
    { value: "platform_user", label: "Any Internal Platform User" },
  ],
};

function WorkflowActionBuilder() {
  const navigate = useNavigate();
  const { workflowId } = useParams();

  const [saving, setSaving] = useState(false);

  const [workflowScope, setWorkflowScope] = useState("account");
  const [actionType, setActionType] = useState("");
  const [offsetAmount, setOffsetAmount] = useState("");
  const [offsetUnit, setOffsetUnit] = useState("days");
  const [offsetDirection, setOffsetDirection] = useState("after_previous");
  const [sendTo, setSendTo] = useState("record");

  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [emailTemplateId, setEmailTemplateId] = useState("");
  const [smsTemplateId, setSmsTemplateId] = useState("");
  const [tagName, setTagName] = useState("");
  const [statusValue, setStatusValue] = useState("");

  const currentEmailRecipientOptions =
    EMAIL_RECIPIENT_OPTIONS[workflowScope] || EMAIL_RECIPIENT_OPTIONS.account;

  const resetActionFields = (nextActionType) => {
    setActionType(nextActionType);
    setOffsetAmount("");
    setOffsetUnit("days");
    setOffsetDirection("after_previous");
    setSendTo(
      (EMAIL_RECIPIENT_OPTIONS[workflowScope] &&
        EMAIL_RECIPIENT_OPTIONS[workflowScope][0] &&
        EMAIL_RECIPIENT_OPTIONS[workflowScope][0].value) ||
        "record"
    );
    setTaskTitle("");
    setTaskDescription("");
    setEmailTemplateId("");
    setSmsTemplateId("");
    setTagName("");
    setStatusValue("");
  };

  const handleScopeChange = (nextScope) => {
    setWorkflowScope(nextScope);
    setSendTo(
      (EMAIL_RECIPIENT_OPTIONS[nextScope] &&
        EMAIL_RECIPIENT_OPTIONS[nextScope][0] &&
        EMAIL_RECIPIENT_OPTIONS[nextScope][0].value) ||
        "record"
    );
  };

  const buildSummary = () => {
    if (!actionType) return "Choose an action type to see a summary.";

    if (actionType === "send_email") {
      const recipient = currentEmailRecipientOptions.find(
        (opt) => opt.value === sendTo
      );
      return `${recipient ? recipient.label : sendTo} · Template ID ${
        emailTemplateId || "not set"
      }`;
    }

    if (actionType === "send_sms") {
      return `SMS Template ID ${smsTemplateId || "not set"}`;
    }

    if (actionType === "create_task") {
      return taskTitle || "Task title not set";
    }

    if (actionType === "add_tag" || actionType === "remove_tag") {
      return tagName || "Tag not set";
    }

    if (actionType === "update_status") {
      return statusValue || "Status not set";
    }

    return "Action configured.";
  };

  const handleSaveAction = async () => {
    if (!workflowId) {
      alert("Invalid workflow ID.");
      return;
    }

    if (!actionType) {
      alert("Please select an action type.");
      return;
    }

    if (actionType === "send_email" && !emailTemplateId) {
      alert("Please enter an email template ID.");
      return;
    }

    if (actionType === "create_task" && !taskTitle.trim()) {
      alert("Please enter a task title.");
      return;
    }

    if (
      (actionType === "add_tag" || actionType === "remove_tag") &&
      !tagName.trim()
    ) {
      alert("Please enter a tag.");
      return;
    }

    if (actionType === "update_status" && !statusValue.trim()) {
      alert("Please enter a status.");
      return;
    }

    try {
      setSaving(true);

      const token = localStorage.getItem("token");

      const payload = {
        action_type: actionType,
        offset_amount: offsetAmount ? Number(offsetAmount) : null,
        offset_unit: offsetUnit,
        offset_direction: offsetDirection,
        send_to: actionType === "send_email" ? sendTo : null,
        task_title: actionType === "create_task" ? taskTitle || null : null,
        task_description:
          actionType === "create_task" ? taskDescription || null : null,
        email_template_id:
          actionType === "send_email" ? emailTemplateId || null : null,
        sms_template_id:
          actionType === "send_sms" ? smsTemplateId || null : null,
        tag:
          actionType === "add_tag" || actionType === "remove_tag"
            ? tagName || null
            : null,
        status: actionType === "update_status" ? statusValue || null : null,
      };

      const res = await fetch(
        `${API_BASE_URL}/api/automations/workflows/${workflowId}/actions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const errorText = await res.text();
        console.error("❌ Action save error:", errorText);
        throw new Error("Failed to save action");
      }

      alert("Action saved successfully.");
      navigate(`/automations/workflows/${workflowId}`);
    } catch (err) {
      console.error("❌ Action save error:", err);
      alert(err.message || "Error saving action.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page workflow-page">
        <div className="sales-hero workflow-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">Add Workflow Action</h1>
            <p className="sales-subtitle">
              Create a standalone workflow action and attach it to an existing
              automation.
            </p>
          </div>

          <div className="sales-header-actions">
            <button
              className="sales-secondary-btn"
              onClick={() => navigate(`/automations/workflows/${workflowId}`)}
            >
              Back to Workflow
            </button>

            <button
              className="sales-primary-btn"
              onClick={handleSaveAction}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Action"}
            </button>
          </div>
        </div>

        <div className="workflow-layout">
          <div className="workflow-main-column">
            <div className="workflow-card">
              <div className="workflow-card-head">
                <div>
                  <h2>Action Setup</h2>
                  <p>
                    Choose the workflow scope and define what kind of action
                    this step should perform.
                  </p>
                </div>
              </div>

              <div className="workflow-form-grid">
                <div className="workflow-field">
                  <label>Workflow Scope</label>
                  <select
                    className="workflow-input"
                    value={workflowScope}
                    onChange={(e) => handleScopeChange(e.target.value)}
                  >
                    <option value="account">Account Workflow</option>
                    <option value="platform">Platform Workflow</option>
                  </select>
                </div>

                <div className="workflow-field">
                  <label>Action Type</label>
                  <select
                    className="workflow-input"
                    value={actionType}
                    onChange={(e) => resetActionFields(e.target.value)}
                  >
                    <option value="">Select action</option>
                    <option value="send_email">Send Email</option>
                    <option value="send_sms">Send SMS</option>
                    <option value="create_task">Create Task</option>
                    <option value="add_tag">Add Tag</option>
                    <option value="remove_tag">Remove Tag</option>
                    <option value="update_status">Update Status</option>
                  </select>
                </div>
              </div>
            </div>

            {actionType && (
              <>
                <div className="workflow-card">
                  <div className="workflow-card-head">
                    <div>
                      <h2>Action Details</h2>
                      <p>Configure the fields required for this action type.</p>
                    </div>
                  </div>

                  <div className="workflow-form-grid">
                    {actionType === "send_email" && (
                      <>
                        <div className="workflow-field">
                          <label>Send To</label>
                          <select
                            className="workflow-input"
                            value={sendTo}
                            onChange={(e) => setSendTo(e.target.value)}
                          >
                            {currentEmailRecipientOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="workflow-field">
                          <label>Email Template ID</label>
                          <input
                            type="number"
                            className="workflow-input"
                            placeholder="Email Template ID"
                            value={emailTemplateId}
                            onChange={(e) => setEmailTemplateId(e.target.value)}
                          />
                        </div>
                      </>
                    )}

                    {actionType === "send_sms" && (
                      <div className="workflow-field workflow-field-full">
                        <label>SMS Template ID</label>
                        <input
                          type="number"
                          className="workflow-input"
                          placeholder="SMS Template ID"
                          value={smsTemplateId}
                          onChange={(e) => setSmsTemplateId(e.target.value)}
                        />
                      </div>
                    )}

                    {actionType === "create_task" && (
                      <>
                        <div className="workflow-field workflow-field-full">
                          <label>Task Title</label>
                          <input
                            type="text"
                            className="workflow-input"
                            placeholder="Task Title"
                            value={taskTitle}
                            onChange={(e) => setTaskTitle(e.target.value)}
                          />
                        </div>

                        <div className="workflow-field workflow-field-full">
                          <label>Task Description</label>
                          <textarea
                            className="workflow-input workflow-textarea"
                            placeholder="Task Description"
                            value={taskDescription}
                            onChange={(e) => setTaskDescription(e.target.value)}
                          />
                        </div>
                      </>
                    )}

                    {(actionType === "add_tag" ||
                      actionType === "remove_tag") && (
                      <div className="workflow-field workflow-field-full">
                        <label>Tag Name</label>
                        <input
                          type="text"
                          className="workflow-input"
                          placeholder="Tag Name"
                          value={tagName}
                          onChange={(e) => setTagName(e.target.value)}
                        />
                      </div>
                    )}

                    {actionType === "update_status" && (
                      <div className="workflow-field workflow-field-full">
                        <label>Status</label>
                        <input
                          type="text"
                          className="workflow-input"
                          placeholder="Status"
                          value={statusValue}
                          onChange={(e) => setStatusValue(e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="workflow-card">
                  <div className="workflow-card-head">
                    <div>
                      <h2>Timing</h2>
                      <p>
                        Set when this action should run relative to the trigger
                        or previous action.
                      </p>
                    </div>
                  </div>

                  <div className="workflow-form-grid">
                    <div className="workflow-field">
                      <label>Offset Amount</label>
                      <input
                        type="number"
                        className="workflow-input"
                        placeholder="Amount"
                        value={offsetAmount}
                        onChange={(e) => setOffsetAmount(e.target.value)}
                      />
                    </div>

                    <div className="workflow-field">
                      <label>Offset Unit</label>
                      <select
                        className="workflow-input"
                        value={offsetUnit}
                        onChange={(e) => setOffsetUnit(e.target.value)}
                      >
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                      </select>
                    </div>

                    <div className="workflow-field workflow-field-full">
                      <label>Timing Reference</label>
                      <select
                        className="workflow-input"
                        value={offsetDirection}
                        onChange={(e) => setOffsetDirection(e.target.value)}
                      >
                        <option value="after_previous">
                          After Previous Action
                        </option>
                        <option value="after">After Trigger</option>
                        <option value="before">Before Trigger</option>
                      </select>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="workflow-side-column">
            <div className="workflow-card workflow-sticky">
              <div className="workflow-card-head">
                <div>
                  <h2>Action Preview</h2>
                  <p>A quick summary of the action you are about to save.</p>
                </div>
              </div>

              <div className="workflow-step-list">
                <div className="workflow-step-card">
                  <div className="workflow-step-badge">Draft</div>

                  <div className="workflow-step-content">
                    <div className="workflow-step-title">
                      {actionType
                        ? actionType.replace(/_/g, " ")
                        : "No action selected"}
                    </div>

                    <div className="workflow-step-summary">
                      {buildSummary()}
                    </div>

                    {actionType && (
                      <div className="workflow-step-meta">
                        {offsetAmount ? `${offsetAmount} ${offsetUnit}` : "No delay"} ·{" "}
                        {offsetDirection.replace(/_/g, " ")}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="workflow-footer-save">
                <button
                  className="sales-primary-btn workflow-save-btn"
                  onClick={handleSaveAction}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Action"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default WorkflowActionBuilder;